import { useState } from "react"
import classNames from "classnames"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ButtonSecondary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { SatelliteTemporalBias } from "@nova/constants"
import { temporalBiasOptions } from "./SatellitePluginActionBody"

export interface SatelliteDateDirectionModalProps {
    closeModal: () => void
}

const descriptions: Record<SatelliteTemporalBias, string> = {
    before: "Uses the most recent available scenes on or before the selected date.",
    closest: "Uses whichever scenes, before or after, fall nearest the selected date.",
    after: "Uses the next available scenes on or after the selected date.",
}

// Left-to-right display order for the illustration below; temporalBiasOptions is ordered for the Select dropdown instead.
const modalOrder: SatelliteTemporalBias[] = ["before", "closest", "after"]

// Modal is fixed at 690px; NovaModalContainer's own px-8 padding leaves this much for content.
const GRAPHIC_WIDTH = 626
const GRAPHIC_HEIGHT = 168
const GRAPHIC_CENTER_X = GRAPHIC_WIDTH / 2
const BADGE_OFFSET_X = 149.9
const BADGE_X: Record<SatelliteTemporalBias, number> = {
    before: GRAPHIC_CENTER_X - BADGE_OFFSET_X,
    closest: GRAPHIC_CENTER_X,
    after: GRAPHIC_CENTER_X + BADGE_OFFSET_X,
}

// The horizontal span of dots each arc passes over, used to highlight them on hover.
const DOT_MARGIN = 17.75
const ARC_SPAN: Record<SatelliteTemporalBias, [number, number]> = {
    before: [DOT_MARGIN, GRAPHIC_CENTER_X],
    after: [GRAPHIC_CENTER_X, GRAPHIC_WIDTH - DOT_MARGIN],
    closest: [BADGE_X.before, BADGE_X.after],
}

// Icon badges fall in left-to-right starting at 0.5s, 0.2s apart; each icon-fall-in takes 0.6s, so labels fade in right as their icon lands.
const ICON_FALL_START = 0.5
const ICON_FALL_STAGGER = 0.2
const ICON_FALL_DURATION = 0.6
const LABEL_FADE_DURATION = 0.4
const CENTER_FADE_DURATION = 0.5

function DirectionTimeline({
    hovered,
    onHover,
}: {
    hovered: SatelliteTemporalBias | null
    onHover: (value: SatelliteTemporalBias | null) => void
}) {
    const dotCount = 17
    const dotStep = (GRAPHIC_WIDTH - DOT_MARGIN * 2) / (dotCount - 1)
    const dotY = 160.12
    const centerDotIndex = (dotCount - 1) / 2

    const arcStroke = (value: SatelliteTemporalBias) => ({
        stroke: hovered === value ? "#FFFFFF" : "rgba(255,255,255,0.2)",
        transition: "stroke 0.6s ease",
    })

    return (
        <div className="relative" style={{ width: GRAPHIC_WIDTH, height: GRAPHIC_HEIGHT }}>
            {/* Scoped like CreateChecklistModal.tsx's blob-spin keyframes - these three animations are only ever used here, so they don't belong in the shared tailwind.config.js. */}
            <style>{`
                @keyframes arc-draw {
                    0% { stroke-dashoffset: 1; }
                    100% { stroke-dashoffset: 0; }
                }
                @keyframes arc-rise {
                    0% { opacity: 0; transform: translateY(32px); }
                    50% { opacity: 1; }
                    100% { transform: translateY(0); }
                }
                @keyframes icon-fall-in {
                    0% { opacity: 0; transform: translateY(-16px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
            `}</style>
            <svg
                width={GRAPHIC_WIDTH}
                height={GRAPHIC_HEIGHT}
                viewBox={`0 0 ${GRAPHIC_WIDTH} ${GRAPHIC_HEIGHT}`}
                style={{ overflow: "visible" }}
                className="absolute top-0 left-0"
            >
                <defs>
                    {/* Mirrors the Figma export's dot glow filter (feGaussianBlur + white/35% recolor) - a plain CSS drop-shadow clips because its default filter region is too tight for this blur radius. */}
                    <filter
                        id="dotGlow"
                        x="-300%"
                        y="-300%"
                        width="700%"
                        height="700%"
                        filterUnits="objectBoundingBox"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity={0} result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feGaussianBlur stdDeviation={5.67797} />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.35 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow"
                        />
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" />
                    </filter>
                </defs>
                {/* Each arc's static repositioning lives on a wrapping <g> so the animated path's own CSS transform (arc-rise) doesn't clobber it - an animated CSS transform fully overrides a presentation-attribute transform on the same element. */}
                {/* "before" and "after" arcs both start at the centre dot and draw outward to their edge dot. */}
                <g transform={`translate(${DOT_MARGIN - 18.3158} -34.1699)`}>
                    <path
                        d="M313.567 193.153C313.567 111.62 247.472 45.5254 165.94 45.5254C84.4074 45.5254 18.3125 111.62 18.3125 193.153"
                        fill="none"
                        strokeWidth={1.13559}
                        pathLength={1}
                        strokeDasharray={1}
                        style={{
                            ...arcStroke("before"),
                            animation: "arc-draw 1s ease-out forwards",
                        }}
                    />
                    <path
                        d="M313.57 195.424C313.57 113.264 379.665 46.6611 461.197 46.6611C542.73 46.6611 608.825 113.264 608.825 195.424"
                        fill="none"
                        strokeWidth={1.13559}
                        pathLength={1}
                        strokeDasharray={1}
                        style={{
                            ...arcStroke("after"),
                            animation: "arc-draw 1s ease-out forwards",
                        }}
                    />
                    {/* "closest" arc fades in while rising for its full 1s duration; its keyframe's 50% opacity stop is what CENTER_FADE_DURATION mirrors below for the "Date Selected" pill. */}
                    <path
                        d="M461.2 195.424C461.2 113.264 395.105 46.6611 313.572 46.6611C232.04 46.6611 165.945 113.264 165.945 195.424"
                        fill="none"
                        strokeWidth={1.13559}
                        style={{ ...arcStroke("closest"), animation: "arc-rise 1s ease-out both" }}
                    />
                </g>
                {Array.from({ length: dotCount }, (_, i) => {
                    const x = DOT_MARGIN + i * dotStep
                    const isEndpoint = i === 0 || i === dotCount - 1
                    const isSelected = i === centerDotIndex
                    const fadeDelay = Math.abs(i - centerDotIndex) * 0.1
                    if (!isEndpoint && !isSelected) {
                        const isUnderHoveredArc =
                            !!hovered &&
                            x >= ARC_SPAN[hovered][0] - 0.01 &&
                            x <= ARC_SPAN[hovered][1] + 0.01
                        return (
                            <circle
                                key={i}
                                cx={x}
                                cy={dotY}
                                r={2.271}
                                fill={isUnderHoveredArc ? "#404044" : "#28282C"}
                                style={{
                                    transition: "fill 0.6s ease",
                                    animationDelay: `${fadeDelay}s`,
                                    animationDuration: "0.2s",
                                }}
                                className="animate-fade-in"
                            />
                        )
                    }
                    return (
                        <g
                            key={i}
                            filter="url(#dotGlow)"
                            className="animate-fade-in"
                            style={{ animationDelay: `${fadeDelay}s`, animationDuration: "0.2s" }}
                        >
                            <circle
                                cx={x}
                                cy={dotY}
                                r={4.5425}
                                fill={isSelected ? "#F1FF66" : "#858C97"}
                                stroke="rgba(255,255,255,0.10)"
                                strokeWidth={2.271}
                            />
                        </g>
                    )
                })}
            </svg>

            {modalOrder.map((value, index) => {
                const option = temporalBiasOptions.find((o) => o.value === value)
                if (!option) return null
                const isHovered = hovered === value
                const iconDelay = ICON_FALL_START + index * ICON_FALL_STAGGER
                const labelDelay = iconDelay + ICON_FALL_DURATION
                return (
                    <div
                        key={value}
                        className="absolute top-0"
                        style={{ left: BADGE_X[value], transform: "translateX(-50%)" }}
                        onMouseEnter={() => onHover(value)}
                        onMouseLeave={() => onHover(null)}
                    >
                        <div
                            className="flex flex-col items-center gap-[2px]"
                            style={{ animation: `icon-fall-in 0.6s ease-out ${iconDelay}s both` }}
                        >
                            <div
                                className={classNames(
                                    "rounded-full bg-gray-900 flex items-center justify-center transition-colors duration-600 border-[1.136px]",
                                    isHovered ? "border-white" : "border-white/5"
                                )}
                                style={{ width: 22.712, height: 22.712, padding: 4.038 }}
                            >
                                <div className="relative w-[10px] h-[10px] flex items-center justify-center">
                                    <option.Icon
                                        className={classNames(
                                            "absolute transition-colors duration-600",
                                            isHovered ? "text-white" : "text-gray-300"
                                        )}
                                        style={{ transform: `scale(${10 / 24})` }}
                                    />
                                </div>
                            </div>
                            <span
                                className={classNames(
                                    "text-[11px] tracking-[2.271px] uppercase whitespace-nowrap transition-colors duration-600 animate-fade-in",
                                    isHovered ? "text-lazeryellow" : "text-gray-500"
                                )}
                                style={{
                                    animationDelay: `${labelDelay}s`,
                                    animationDuration: `${LABEL_FADE_DURATION}s`,
                                }}
                            >
                                {option.label}
                            </span>
                        </div>
                    </div>
                )
            })}

            <div
                className="absolute top-[117px] left-1/2 -translate-x-1/2 bg-black-300/20 backdrop-blur-sm border border-white/5 px-1 py-[1px] animate-fade-in"
                style={{ animationDuration: `${CENTER_FADE_DURATION}s` }}
            >
                <small className="tracking-[2.271px] text-gray-300 mono uppercase whitespace-nowrap">
                    Date Selected
                </small>
            </div>
        </div>
    )
}

export const SatelliteDateDirectionModal = ({ closeModal }: SatelliteDateDirectionModalProps) => {
    const [hovered, setHovered] = useState<SatelliteTemporalBias | null>(null)

    return (
        <HighPriorityNovaModalContainer
            icon={Icons.Information}
            title="How Date Direction Works"
            hideTitleUnderline
            bottomBar={
                <div className="flex w-full justify-end">
                    <ButtonSecondary onClick={closeModal} title="Okay" />
                </div>
            }
            closeModal={closeModal}
            className="opacity-90 w-[690px]"
        >
            <div className="flex flex-col pt-4 gap-6" style={{ width: GRAPHIC_WIDTH }}>
                <DirectionTimeline hovered={hovered} onHover={setHovered} />
                <div className="flex gap-6 px-4 pt-8 border-t border-t border-white/10">
                    {modalOrder.map((value) => {
                        const option = temporalBiasOptions.find((o) => o.value === value)
                        if (!option) return null
                        return (
                            <div
                                key={value}
                                className={classNames(
                                    "flex-1 flex flex-col gap-3 transition-opacity duration-500",
                                    hovered && hovered !== value ? "opacity-50" : "opacity-100"
                                )}
                                onMouseEnter={() => setHovered(value)}
                                onMouseLeave={() => setHovered(null)}
                            >
                                <Icons.Icon Icon={option.Icon} iconClassName="text-gray-300" />
                                <small className="flex items-center text-gray-0 uppercase tracking-[1.2px]">
                                    {option.label}
                                </small>
                                <p className="text-[14px] font-normal self-stretch text-gray-300">
                                    {descriptions[value]}
                                </p>
                            </div>
                        )
                    })}
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}
